import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getActorDisplayName,
  USER_FEEDBACK_TYPES,
  USER_FEEDBACK_TYPE_LABELS,
  USER_FEEDBACK_ATTACHMENT_TYPE_LABELS,
} from '@safetyhub/shared';
import { Button, Card, DateInput, Modal, useConfirmDialog } from '@safetyhub/ui';
import { Tooltip } from '../components/Tooltip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  extractApiError,
  type UserFeedback,
  type UserFeedbackType,
  type UserFeedbackStatus,
  type UserFeedbackPriority,
  type NotificationSetting,
  type FeedbackNotificationChannel,
} from '../lib/api';
import { useAuthStore } from '../store/auth';
import { useToastStore } from '../stores/toast';
import { ImageLightbox } from '../components/ImageLightbox';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { Badge } from '../components/Badge';
import { formatDate } from '../utils/format';
import { localDayEndIso, localDayStartIso } from '../lib/localDayBounds';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

/**
 * 배지 — **문구는 shared가 정본**이고(`USER_FEEDBACK_TYPE_LABELS`) 여기서 정하는 것은 색뿐이다
 * (2026-10-12 리뷰 ①: 같은 맵이 네 벌 손나열이라 web과 문구가 갈려 있었다).
 * 색만 `Record<UserFeedbackType, string>`으로 두면 값이 늘 때 tsc가 미기재를 잡는다.
 *
 * ## ⚠️ 2026-10-12 이전 `FEATURE_REQUEST`를 「새 기능 요청」으로 읽지 말 것
 * prod 39건(2026-03-24~09-17)은 **제출 화면이 그 칸을 「개선 요청」으로 표시하던 시절**의 것이라
 * 「새 기능」과 「개선」 두 의도가 한 칸에 있다. **재분류하지 않는다**(제출자 의도를 사후에 알 수 없다).
 * 근거·분포의 정본은 shared `UserFeedbackType`·`USER_FEEDBACK_TYPE_LABELS` JSDoc이다 —
 * 같은 문장을 여기 다시 적지 않는다(한 벌만 두어야 낡지 않는다).
 */
const FEEDBACK_TYPE_COLORS: Record<UserFeedbackType, string> = {
  BUG_REPORT: 'bg-red-100 text-red-800',
  FEATURE_REQUEST: 'bg-blue-100 text-blue-800',
  // `IMPROVEMENT`는 2026-10-12에 늘린 칸이다 — 「새 기능」(`FEATURE_REQUEST`)과 이웃한 의도라
  // 같은 계열의 다른 색으로 둔다(prod의 옛 `FEATURE_REQUEST` 39건은 둘이 섞여 있다 —
  // shared `UserFeedbackType` JSDoc 참조).
  IMPROVEMENT: 'bg-indigo-100 text-indigo-800',
  GENERAL: 'bg-gray-100 text-gray-800',
};

const FEEDBACK_STATUS: Record<UserFeedbackStatus, { label: string; color: string }> = {
  PENDING: { label: '대기', color: 'bg-yellow-100 text-yellow-800' },
  REVIEWING: { label: '검토중', color: 'bg-blue-100 text-blue-800' },
  ACCEPTED: { label: '수락', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: '거절', color: 'bg-red-100 text-red-800' },
  RESOLVED: { label: '완료', color: 'bg-gray-100 text-gray-800' },
};

const FEEDBACK_PRIORITY: Record<UserFeedbackPriority, { label: string; color: string }> = {
  LOW: { label: '낮음', color: 'bg-gray-100 text-gray-600' },
  NORMAL: { label: '보통', color: 'bg-blue-100 text-blue-700' },
  HIGH: { label: '높음', color: 'bg-orange-100 text-orange-700' },
  URGENT: { label: '긴급', color: 'bg-red-100 text-red-700' },
};

// 첨부 종류 라벨 — shared 정본(`USER_FEEDBACK_ATTACHMENT_TYPE_LABELS`). 종전엔 여기 손나열이고
// 타입이 `Record<string, string>`이라 **enum 값이 늘어도 미기재를 못 잡았다**(2026-10-12 리뷰 ①).

type TabKey = 'list' | 'notifications';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function getTypeInfo(type: string) {
  const label = USER_FEEDBACK_TYPE_LABELS[type as UserFeedbackType];
  // 모르는 값은 원문을 그대로 보여 준다 — 서버 enum이 앞서 늘었을 때 화면이 비지 않게.
  if (!label) return { label: type, color: 'bg-gray-100 text-gray-800' };
  return { label, color: FEEDBACK_TYPE_COLORS[type as UserFeedbackType] };
}
function getStatusInfo(status: string) {
  return FEEDBACK_STATUS[status as UserFeedbackStatus] ?? { label: status, color: 'bg-gray-100 text-gray-800' };
}
function getPriorityInfo(priority: string) {
  return FEEDBACK_PRIORITY[priority as UserFeedbackPriority] ?? { label: priority, color: 'bg-gray-100 text-gray-800' };
}

// ─────────────────────────────────────────────
// Attachment Thumbnail (인증 헤더 포함 fetch)
// ─────────────────────────────────────────────

function AttachmentThumb({ feedbackId, attachmentId, accessToken }: { feedbackId: string; attachmentId: string; accessToken: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const url = `${API_HOST}/api/v1/admin/user-feedback/${feedbackId}/attachments/${attachmentId}`;
    fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        if (!res.ok) throw new Error('load failed');
        return res.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => setFailed(true));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [feedbackId, attachmentId, accessToken]);

  if (failed) {
    return <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">이미지 없음</div>;
  }
  if (!src) {
    return <div className="w-full h-full bg-gray-200 animate-pulse" />;
  }
  return <img src={src} alt="" className="w-full h-full object-cover" />;
}

// ─────────────────────────────────────────────
// Detail Modal
// ─────────────────────────────────────────────

interface DetailModalProps {
  feedbackId: string;
  onClose: () => void;
}

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function DetailModal({ feedbackId, onClose }: DetailModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const { accessToken } = useAuthStore();
  const [editForm, setEditForm] = useState<{
    type: string;
    status: string;
    priority: string;
    adminNote: string;
  }>({ type: '', status: '', priority: '', adminNote: '' });
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = useState(false);

  const { data: response, isLoading } = useQuery({
    queryKey: ['admin', 'user-feedback', feedbackId],
    queryFn: () => adminApi.getUserFeedbackDetail(feedbackId),
  });

  const feedback: UserFeedback | null = response?.data ?? null;

  useEffect(() => {
    if (feedback) {
      setEditForm({
        type: feedback.type,
        status: feedback.status,
        priority: feedback.priority,
        adminNote: feedback.adminNote ?? '',
      });
    }
  }, [feedback]);

  const updateMutation = useMutation({
    mutationFn: (data: { type?: UserFeedbackType; status?: UserFeedbackStatus; priority?: UserFeedbackPriority; adminNote?: string }) =>
      adminApi.updateUserFeedback(feedbackId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-feedback'] });
      showToast('피드백이 업데이트되었습니다.', 'success');
      onClose();
    },
    onError: (error) => {
      showToast(extractApiError(error).message, 'error');
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      ...(editForm.type && { type: editForm.type as UserFeedbackType }),
      ...(editForm.status && { status: editForm.status as UserFeedbackStatus }),
      ...(editForm.priority && { priority: editForm.priority as UserFeedbackPriority }),
      adminNote: editForm.adminNote,
    });
  };

  const handleImageClick = async (fid: string, attachments: UserFeedback['attachments'], clickedId: string) => {
    const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const clickedIndex = attachments.findIndex((a) => a.id === clickedId);
    try {
      const urls = await Promise.all(
        attachments.map(async (att) => {
          const url = `${API_HOST}/api/v1/admin/user-feedback/${fid}/attachments/${att.id}`;
          const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
          if (!res.ok) throw new Error('이미지 로드 실패');
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        }),
      );
      // 이전 lightbox blob URL 해제
      if (lightbox) {
        lightbox.urls.forEach((u) => URL.revokeObjectURL(u));
      }
      setLightbox({ urls, index: clickedIndex >= 0 ? clickedIndex : 0 });
    } catch {
      showToast('이미지를 불러올 수 없습니다.', 'error');
    }
  };

  const closeLightbox = () => {
    if (lightbox) {
      lightbox.urls.forEach((u) => URL.revokeObjectURL(u));
      setLightbox(null);
    }
  };

  const diagnostics = feedback?.diagnostics as Record<string, unknown> | null | undefined;
  const isMutating = updateMutation.isPending;

  // 미저장 닫기 보호 — 「관리자 처리」 네 칸이 불러온 값(위 useEffect가 채우는 식)과 다를 때만.
  // 불러오기 전에는 칸이 그려지지 않으므로 적던 내용이 없다.
  const isDirty =
    !!feedback &&
    (editForm.type !== feedback.type ||
      editForm.status !== feedback.status ||
      editForm.priority !== feedback.priority ||
      editForm.adminNote !== (feedback.adminNote ?? ''));

  // 층은 base 기본(z-[70])이다 — 종전 z-[9998]/[9999]는 정책(70→80→90→100) 밖 값이었다. 라이트박스는
  // `z-[10000]`이라 그대로 위에 뜬다. 라이트박스가 열려 있는 동안 Esc는 라이트박스만 닫아야 하므로
  // (둘 다 document keydown을 듣는다) 그동안 이 모달의 Esc를 끈다 — 종전 raw 오버레이엔 Esc가 없어
  // 같은 결과였다. 저장 중에는 닫는 길 전부를 막는다. 폭은 base 최대(max-w-2xl)보다 넓은 종전
  // max-w-3xl을 `!`로 고정한다(같은 property 덧칠의 승자를 출력 순서에 맡기지 않는다).
  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={
          <span className="flex items-center gap-2">
            피드백 상세
            {feedback && (
              <span className="text-sm font-normal font-mono text-gray-400">#{feedback.feedbackNumber}</span>
            )}
          </span>
        }
        ariaLabel="피드백 상세"
        size="xl"
        panelClassName="!max-w-3xl"
        closeOnOverlay={!isMutating}
        closeOnEsc={!isMutating && !lightbox}
        showCloseButton={!isMutating}
        closeLocked={isMutating}
        isDirty={isDirty}
      >
          {isLoading && <div className="p-8 text-center text-gray-500">불러오는 중...</div>}

          {feedback && (
            <div className="space-y-5">
              {/* 기본 정보 */}
              <div className="flex flex-wrap gap-2 items-center">
                <Badge label={getTypeInfo(feedback.type).label} className={getTypeInfo(feedback.type).color} />
                <Badge label={getStatusInfo(feedback.status).label} className={getStatusInfo(feedback.status).color} />
                <Badge label={getPriorityInfo(feedback.priority).label} className={getPriorityInfo(feedback.priority).color} />
                <span className="text-xs text-gray-500 ml-auto">{formatDate(feedback.createdAt)}</span>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 text-base mb-1">{feedback.title}</h3>
                {feedback.description && (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{feedback.description}</p>
                )}
              </div>

              {/* 제출자 정보 */}
              <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
                <div className="flex gap-4">
                  <span className="text-gray-500 w-16 shrink-0">제출자</span>
                  <span className="text-gray-900">{getActorDisplayName({ name: feedback.user.member?.name, deletedAt: feedback.user.deletedAt }, '-')}</span>
                </div>
                {/* 서버 응답 키는 `company`다(`user-feedback.service.ts` 전부 `company: true`).
                    2026-10-10까지 `feedback.organization`을 읽어 **항상 falsy**였고 이 블록이
                    렌더되지 않았다 — 조건부 렌더라 조용했다. 탐지기는 admin 렌더 spec이다.

                    ⚠️ **조건부 렌더를 걷어 늘 렌더한다**(2026-10-12 리뷰 ③). 값 없음은 `?? '-'`로
                    떨어뜨린다 — 같은 화면의 목록이 이미 그렇게 하므로(아래 `<td>`) 조건부로 두면
                    **두 surface가 「조직 없음」에 다르게 답한다**(한쪽은 `-`, 한쪽은 자리째 사라짐).
                    값이 비는 쪽은 자리가 남아 눈에 보이지만 **자리가 사라지면 아무 신호가 없고**,
                    그 침묵이 위 결함을 수개월 잠복시킨 바로 그 형상이다
                    (CLAUDE.md 「존재가 상태를 뜻하는 요소는 조건부 렌더 금지」).
                    스키마는 `company_id` NOT NULL이라 null은 응답 형상이 바뀔 때만 오는데, 그
                    심층 방어는 `?? '-'`가 그대로 맡는다 — 잃는 것은 침묵뿐이다. */}
                <div className="flex gap-4">
                  <span className="text-gray-500 w-16 shrink-0">조직</span>
                  <span className="text-gray-900">{feedback.company?.name ?? '-'}</span>
                </div>
                {feedback.pageUrl && (
                  <div className="flex gap-4">
                    <span className="text-gray-500 w-16 shrink-0">URL</span>
                    <span className="text-gray-700 break-all text-xs">{feedback.pageUrl}</span>
                  </div>
                )}
              </div>

              {/* 첨부 파일 */}
              {feedback.attachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">첨부 파일</h4>
                  <div className="flex flex-wrap gap-3">
                    {feedback.attachments.map((att) => (
                      <div key={att.id} className="text-center">
                        <button
                          className="block w-24 h-24 bg-gray-100 rounded border border-gray-200 overflow-hidden hover:opacity-80 transition-opacity"
                          onClick={() => handleImageClick(feedback.id, feedback.attachments, att.id)}
                          title={att.filename}
                        >
                          <AttachmentThumb
                            feedbackId={feedback.id}
                            attachmentId={att.id}
                            accessToken={accessToken ?? ''}
                          />
                        </button>
                        <span className="text-xs text-gray-500 mt-1 block">
                          {USER_FEEDBACK_ATTACHMENT_TYPE_LABELS[att.type] ?? att.type}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 진단 데이터 */}
              {diagnostics && (
                <div className="border rounded">
                  <button
                    className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsDiagnosticsOpen((v) => !v)}
                  >
                    <span>진단 데이터</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${isDiagnosticsOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isDiagnosticsOpen && (
                    <div className="border-t p-4 space-y-3">
                      {(() => {
                        const browserInfo = diagnostics.browserInfo as Record<string, unknown> | undefined;
                        const ua = browserInfo?.userAgent ?? browserInfo?.platform;
                        return ua ? (
                          <div>
                            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">브라우저/OS</span>
                            <p className="text-xs text-gray-700 mt-1 break-all">
                              {String(ua)}
                            </p>
                          </div>
                        ) : null;
                      })()}
                      {Array.isArray(diagnostics.consoleErrors) && diagnostics.consoleErrors.length > 0 && (
                        <div>
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">콘솔 로그</span>
                          <div className="mt-1 p-2 bg-gray-900 text-xs rounded overflow-x-auto max-h-48 space-y-1">
                            {(diagnostics.consoleErrors as Array<Record<string, unknown>>).map((entry, i) => {
                              const level = String(entry.level ?? 'log');
                              const colorClass = level === 'error' ? 'text-red-300' : level === 'warn' ? 'text-yellow-300' : 'text-gray-300';
                              return (
                                <div key={i} className={colorClass}>
                                  <span className="text-gray-500">[{level.toUpperCase()}]</span>{' '}
                                  {entry.timestamp ? <span className="text-gray-500">{String(entry.timestamp).substring(11, 19)}</span> : null}{' '}
                                  {String(entry.message ?? '')}
                                  {entry.stack ? <div className="text-gray-500 ml-4 text-[10px]">{String(entry.stack)}</div> : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {Array.isArray(diagnostics.networkRequests) && diagnostics.networkRequests.length > 0 && (
                        <div>
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">네트워크 요청</span>
                          <div className="mt-1 overflow-x-auto">
                            <table className="text-xs w-full border-collapse">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="border px-2 py-1 text-left">Method</th>
                                  <th className="border px-2 py-1 text-left">URL</th>
                                  <th className="border px-2 py-1 text-left">Status</th>
                                  <th className="border px-2 py-1 text-left">Duration</th>
                                  <th className="border px-2 py-1 text-left">Size</th>
                                  <th className="border px-2 py-1 text-left">Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(diagnostics.networkRequests as Array<Record<string, unknown>>).map((req, i) => {
                                  const status = Number(req.status ?? 0);
                                  const statusColor = status >= 400 ? 'text-red-600 font-medium' : status === 0 ? 'text-gray-400' : '';
                                  const errorInfo = req.errorInfo as Record<string, unknown> | undefined;
                                  return (
                                    <tr key={i} className="hover:bg-gray-50">
                                      <td className="border px-2 py-1">{String(req.method ?? '-')}</td>
                                      <td className="border px-2 py-1 break-all max-w-xs">{String(req.url ?? '-')}</td>
                                      <td className={`border px-2 py-1 ${statusColor}`}>
                                        {String(req.status ?? '-')}
                                        {req.statusText ? ` ${req.statusText}` : ''}
                                      </td>
                                      <td className="border px-2 py-1">{req.duration != null ? `${req.duration}ms` : '-'}</td>
                                      <td className="border px-2 py-1">
                                        {req.responseSize != null ? `${Number(req.responseSize) >= 1024 ? `${(Number(req.responseSize) / 1024).toFixed(1)}KB` : `${req.responseSize}B`}` : '-'}
                                      </td>
                                      <td className="border px-2 py-1 text-red-600 break-all max-w-xs">
                                        {errorInfo ? `${errorInfo.code ? `[${errorInfo.code}] ` : ''}${errorInfo.message ?? ''}` : '-'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      <details>
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">원본 JSON 보기</summary>
                        <pre className="mt-1 p-2 bg-gray-50 text-xs rounded overflow-x-auto max-h-48 border">
                          {JSON.stringify(diagnostics, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              )}

              {/* 처리 이력 */}
              {(feedback.resolvedAt || feedback.resolvedBy) && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">처리 완료:</span>{' '}
                  {feedback.resolvedBy && <span>{getActorDisplayName({ name: feedback.resolvedBy.member?.name, deletedAt: feedback.resolvedBy.deletedAt }, '-')} · </span>}
                  {feedback.resolvedAt && <span>{formatDate(feedback.resolvedAt)}</span>}
                </div>
              )}

              {/* 관리자 액션 */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">관리자 처리</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">유형</label>
                    <select
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={editForm.type}
                      onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}
                    >
                      {Object.entries(USER_FEEDBACK_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">상태</label>
                    <select
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={editForm.status}
                      onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      {Object.entries(FEEDBACK_STATUS).map(([value, { label }]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">우선순위</label>
                    <select
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={editForm.priority}
                      onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))}
                    >
                      {Object.entries(FEEDBACK_PRIORITY).map(([value, { label }]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">관리자 메모</label>
                  <textarea
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
                    rows={3}
                    placeholder="내부 메모 입력..."
                    value={editForm.adminNote}
                    onChange={(e) => setEditForm((f) => ({ ...f, adminNote: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <ModalCancelButton onCancel={onClose} disabled={isMutating} size="sm" />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={isMutating}
                  >
                    {isMutating ? '저장 중...' : '저장'}
                  </Button>
                </div>
              </div>
            </div>
          )}
      </Modal>

      {/* 전체화면 라이트박스 */}
      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          initialIndex={lightbox.index}
          onClose={closeLightbox}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────
// Notification Settings
// ─────────────────────────────────────────────

/**
 * 알림설정의 유형 체크박스 목록 — **shared 상수에서 파생**한다(2026-10-12 리뷰 ①).
 * 종전엔 손나열 **배열**이라 ① 문구가 위 배지 맵과 따로 적혀 있었고 ② `Record`가 아니어서
 * **값이 늘면 조용히 빠졌다**(같은 화면의 형제인 옛 `FEEDBACK_TYPES`는 `Record`라 red가 됐다 —
 * 한 화면에서 한쪽만 미기재를 잡는 상태였다). 파생시키면 둘 다 구조적으로 불가능해진다.
 */
const FEEDBACK_TYPE_OPTIONS: { value: UserFeedbackType; label: string }[] = USER_FEEDBACK_TYPES.map(
  (value) => ({ value, label: USER_FEEDBACK_TYPE_LABELS[value] }),
);

interface NotificationFormData {
  channel: FeedbackNotificationChannel;
  target: string;
  label: string;
  isActive: boolean;
  feedbackTypes: UserFeedbackType[];
}

const EMPTY_FORM: NotificationFormData = {
  channel: 'SLACK',
  target: '',
  label: '',
  isActive: true,
  feedbackTypes: [],
};

function NotificationSettings() {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<NotificationFormData>(EMPTY_FORM);

  const { data: response, isLoading } = useQuery({
    queryKey: ['admin', 'user-feedback', 'notification-settings'],
    queryFn: () => adminApi.getNotificationSettings(),
  });

  const settings: NotificationSetting[] = response?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (data: NotificationFormData) =>
      adminApi.createNotificationSetting({
        channel: data.channel,
        target: data.target,
        label: data.label || undefined,
        isActive: data.isActive,
        feedbackTypes: data.feedbackTypes.length > 0 ? data.feedbackTypes : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-feedback', 'notification-settings'] });
      showToast('알림 설정이 추가되었습니다.', 'success');
      setIsFormOpen(false);
      setForm(EMPTY_FORM);
    },
    onError: (error) => {
      showToast(extractApiError(error).message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: NotificationFormData }) =>
      adminApi.updateNotificationSetting(id, {
        target: data.target,
        label: data.label || undefined,
        isActive: data.isActive,
        feedbackTypes: data.feedbackTypes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-feedback', 'notification-settings'] });
      showToast('알림 설정이 수정되었습니다.', 'success');
      setEditingId(null);
      setForm(EMPTY_FORM);
    },
    onError: (error) => {
      showToast(extractApiError(error).message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteNotificationSetting(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-feedback', 'notification-settings'] });
      showToast('알림 설정이 삭제되었습니다.', 'success');
    },
    onError: (error) => {
      showToast(extractApiError(error).message, 'error');
    },
  });

  const handleEdit = (setting: NotificationSetting) => {
    setEditingId(setting.id);
    setForm({
      channel: setting.channel,
      target: setting.target,
      label: setting.label ?? '',
      isActive: setting.isActive,
      feedbackTypes: setting.feedbackTypes,
    });
  };

  const handleSubmit = () => {
    if (!form.target.trim()) {
      showToast('대상(URL 또는 이메일)을 입력하세요.', 'error');
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const handleCancel = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const isFormVisible = isFormOpen || editingId !== null;
  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">알림 대상 목록</h3>
        {!isFormVisible && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => { setIsFormOpen(true); setForm(EMPTY_FORM); }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            추가
          </Button>
        )}
      </div>

      {isFormVisible && (
        <Card surface="soft" elevation="none" className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">{editingId ? '알림 설정 수정' : '새 알림 설정'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">채널</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={form.channel}
                onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value as FeedbackNotificationChannel }))}
                disabled={editingId !== null}
              >
                <option value="SLACK">Slack</option>
                <option value="EMAIL">Email</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                {form.channel === 'SLACK' ? 'Webhook URL' : '이메일 주소'}
              </label>
              <input
                type={form.channel === 'EMAIL' ? 'email' : 'url'}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                placeholder={form.channel === 'SLACK' ? 'https://hooks.slack.com/...' : 'admin@example.com'}
                value={form.target}
                onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">라벨 (선택)</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                placeholder="예: #safety-feedback"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer pb-1.5">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="rounded"
                />
                활성
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">피드백 유형 필터 (비워두면 전체)</label>
            <div className="flex gap-3">
              {FEEDBACK_TYPE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.feedbackTypes.includes(value)}
                    onChange={(e) => {
                      setForm((f) => ({
                        ...f,
                        feedbackTypes: e.target.checked
                          ? [...f.feedbackTypes, value]
                          : f.feedbackTypes.filter((t) => t !== value),
                      }));
                    }}
                    className="rounded"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={handleCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-100">
              취소
            </button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isMutating}
            >
              {isMutating ? '저장 중...' : '저장'}
            </Button>
          </div>
        </Card>
      )}

      {isLoading && <div className="text-center py-8 text-gray-400 text-sm">불러오는 중...</div>}
      {!isLoading && settings.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">알림 설정이 없습니다.</div>
      )}
      {settings.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-left">
                <th className="px-4 py-2 text-xs font-medium text-gray-600">채널</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-600">대상</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-600">라벨</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-600">필터</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-600">상태</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-600">액션</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {settings.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.channel === 'SLACK' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {s.channel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{s.target}</td>
                  <td className="px-4 py-3 text-gray-600">{s.label ?? '-'}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {s.feedbackTypes.length === 0
                      ? '전체'
                      : s.feedbackTypes.map((t) => USER_FEEDBACK_TYPE_LABELS[t as UserFeedbackType] ?? t).join(', ')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {s.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(s)} className="text-xs text-brand-600 hover:text-brand-800">
                        수정
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            await confirm({
                              title: '알림 설정 삭제',
                              body: '알림 설정을 삭제하시겠습니까?',
                              confirmLabel: '삭제',
                              danger: true,
                            })
                          ) {
                            deleteMutation.mutate(s.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────

/**
 * 사용자 피드백 — 목록·상세·알림 설정.
 * 서버의 새 피드백 알림(메일·웹훅)이 `${ADMIN_APP_URL}/user-feedback/<id>` 링크를 보내므로
 * 상세는 **경로가 소유한다**(`App.tsx`의 `user-feedback/:id`) — 그 경로로 열면 상세 모달이 바로 뜬다.
 * 형판은 `OpsIncidents`(같은 링크 규약)다. 상세를 로컬 상태로 되돌리면 링크가 목록만 연다.
 */
export default function UserFeedbackPage() {
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const openDetail = (id: string) => navigate(`/user-feedback/${id}`);
  const closeDetail = () => navigate('/user-feedback');
  const [activeTab, setActiveTab] = useState<TabKey>('list');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 20,
  });
  const { data: response, isLoading } = useQuery({
    queryKey: ['admin', 'user-feedback', 'list', filters],
    queryFn: () =>
      adminApi.getUserFeedbackList({
        type: filters.type || undefined,
        status: filters.status || undefined,
        // 'YYYY-MM-DD'를 그대로 보내면 서버가 UTC 자정으로 읽는다 — 로컬 하루 경계로(lib/localDayBounds).
        dateFrom: localDayStartIso(filters.dateFrom),
        dateTo: localDayEndIso(filters.dateTo),
        page: filters.page,
        limit: filters.limit,
      }),
    placeholderData: (prev) => prev,
  });

  // `getUserFeedbackList`는 `fetchApi<UserFeedback[]>`라 `data`가 이미 `UserFeedback[] | null`이다 —
  // 종전 `as unknown as UserFeedback[]` 이중 캐스트는 노이즈였다(2026-10-12 리뷰 ②).
  // ⚠️ 걷어도 **tsc가 서버 응답 형상을 대조하게 되지는 않는다** — 이 `UserFeedback`은 `lib/api.ts`에
  // 손으로 선언한 타입이고 서버와 공유 계약이 없다. 그 축(2026-10-10 `organization` → `company`
  // 드리프트)의 탐지기는 `apps/server/test/integration/user-feedback.integration.spec.ts`의
  // 「응답 키는 `company`다」이고, 캐스트 제거는 거기에 보탬이 되지 않는다.
  const feedbacks: UserFeedback[] = response?.data ?? [];
  const meta = response?.meta;
  const totalPages = meta ? Math.ceil(meta.total / meta.limit) : 1;

  const handleFilterChange = (key: string, value: string) => {
    setFilters((f) => ({ ...f, [key]: value, page: 1 }));
  };

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'list', label: '피드백 목록' },
    { key: 'notifications', label: '알림 설정' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">사용자 피드백</h1>
        <p className="text-sm text-gray-500 mt-1">
          사용자가 제출한 버그 신고, 기능 요청, 일반 피드백을 관리합니다.
        </p>
      </div>

      {/* 탭 */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 피드백 목록 탭 */}
      {activeTab === 'list' && (
        <div className="space-y-4">
          {/* 필터 */}
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-600 mb-1">유형</label>
              <select
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">전체</option>
                {Object.entries(USER_FEEDBACK_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">상태</label>
              <select
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">전체</option>
                {Object.entries(FEEDBACK_STATUS).map(([value, { label }]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">시작일</label>
              <DateInput
                label="시작일"
                size="sm"
                value={filters.dateFrom}
                onChange={(next) => handleFilterChange('dateFrom', next)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">종료일</label>
              <DateInput
                label="종료일"
                size="sm"
                value={filters.dateTo}
                onChange={(next) => handleFilterChange('dateTo', next)}
              />
            </div>
            {(filters.type || filters.status || filters.dateFrom || filters.dateTo) && (
              <button
                className="text-sm text-gray-500 hover:text-gray-700 underline"
                onClick={() => setFilters({ type: '', status: '', dateFrom: '', dateTo: '', page: 1, limit: 20 })}
              >
                필터 초기화
              </button>
            )}
          </div>

          {/* 테이블 */}
          <Card padding="none" radius="lg" elevation="none" className="overflow-hidden">
            {isLoading && <div className="text-center py-12 text-gray-400">불러오는 중...</div>}
            {!isLoading && feedbacks.length === 0 && (
              <div className="text-center py-12 text-gray-400">피드백이 없습니다.</div>
            )}
            {!isLoading && feedbacks.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left">
                      <th rowSpan={2} className="px-4 py-2 text-xs font-medium text-gray-600 w-10 align-middle">#</th>
                      <th rowSpan={2} className="px-4 py-2 text-xs font-medium text-gray-600 w-20 align-middle">유형</th>
                      <th className="px-4 pt-2 pb-0 text-xs font-medium text-gray-600 w-48">제목</th>
                      <th className="px-4 pt-2 pb-0 text-xs font-medium text-gray-600 w-24">제출자</th>
                      <th className="px-4 pt-2 pb-0 text-xs font-medium text-gray-600 w-28">조직</th>
                      <th className="px-4 pt-2 pb-0 text-xs font-medium text-gray-600 w-16">상태</th>
                      <th className="px-4 pt-2 pb-0 text-xs font-medium text-gray-600 w-16">우선<br />순위</th>
                      <th className="px-4 pt-2 pb-0 text-xs font-medium text-gray-600 w-32">날짜</th>
                    </tr>
                    <tr className="text-left">
                      <th className="px-4 pt-0 pb-2 text-[10px] text-gray-400 font-normal">내용</th>
                      <th colSpan={5} className="px-4 pt-0 pb-2 text-[10px] text-gray-400 font-normal">관리자 메모</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbacks.map((feedback) => {
                      const hasSubRow = !!(feedback.description || feedback.adminNote);
                      return (
                        <React.Fragment key={feedback.id}>
                          <tr
                            className={`hover:bg-gray-50 cursor-pointer ${hasSubRow ? '' : 'border-b border-gray-200'}`}
                            onClick={() => openDetail(feedback.id)}
                          >
                            <td className="px-4 py-3 text-gray-400 text-xs">{feedback.feedbackNumber}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge label={getTypeInfo(feedback.type).label} className={`${getTypeInfo(feedback.type).color} justify-center`} />
                            </td>
                            <td className="px-4 py-3 text-gray-900 max-w-xs truncate">{feedback.title}</td>
                            <td className="px-4 py-3 text-gray-700">{getActorDisplayName({ name: feedback.user.member?.name, deletedAt: feedback.user.deletedAt }, '-')}</td>
                            <td className="px-4 py-3 text-gray-600">{feedback.company?.name ?? '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <Badge label={getStatusInfo(feedback.status).label} className={getStatusInfo(feedback.status).color} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <Badge label={getPriorityInfo(feedback.priority).label} className={getPriorityInfo(feedback.priority).color} />
                            </td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(feedback.createdAt)}</td>
                          </tr>
                          {hasSubRow && (
                            <tr
                              className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                              onClick={() => openDetail(feedback.id)}
                            >
                              <td className="px-4 pb-2.5" />
                              <td className="px-4 pb-2.5 pt-0" />
                              <td className="px-4 pb-2.5 pt-0 max-w-0">
                                <Tooltip content={feedback.description}>
                                  <p className="text-xs text-gray-500 truncate">
                                    {feedback.description || '-'}
                                  </p>
                                </Tooltip>
                              </td>
                              <td colSpan={5} className="px-4 pb-2.5 pt-0 max-w-0">
                                {feedback.adminNote ? (
                                  <Tooltip content={feedback.adminNote}>
                                    <p className="text-xs text-brand-600 font-medium truncate">
                                      📝 {feedback.adminNote}
                                    </p>
                                  </Tooltip>
                                ) : (
                                  <p className="text-xs text-gray-300">메모 없음</p>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                총 {meta?.total ?? 0}건 ({filters.page} / {totalPages} 페이지)
              </span>
              <div className="flex gap-1">
                <button
                  disabled={filters.page <= 1}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                  className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const page = filters.page <= 3 ? i + 1 : filters.page - 2 + i;
                  if (page < 1 || page > totalPages) return null;
                  return (
                    <button
                      key={page}
                      onClick={() => setFilters((f) => ({ ...f, page }))}
                      className={`px-3 py-1 border rounded ${
                        filters.page === page
                          ? 'bg-red-600 text-white border-red-600'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  disabled={filters.page >= totalPages}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                  className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 알림 설정 탭 */}
      {activeTab === 'notifications' && (
        <Card padding="xl" radius="lg" elevation="none">
          <NotificationSettings />
        </Card>
      )}

      {/* 상세 모달 */}
      {routeId && <DetailModal feedbackId={routeId} onClose={closeDetail} />}
    </div>
  );
}
